import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { ArtifactAiHeadingOption } from './ArtifactAiPanel';
import type { AiGenerateBody, AiGenerateScope } from '@/lib/api';

interface ArtifactAiGenerateTabProps {
  headings: ArtifactAiHeadingOption[];
  disabled: boolean;
  isStreaming: boolean;
  onGenerate: (body: AiGenerateBody) => Promise<void>;
}

export function ArtifactAiGenerateTab({
  headings,
  disabled,
  isStreaming,
  onGenerate,
}: ArtifactAiGenerateTabProps): React.ReactElement {
  const [instruction, setInstruction] = useState('');
  const [scope, setScope] = useState<AiGenerateScope>('document');
  const [headingId, setHeadingId] = useState(headings[0]?.id ?? '');
  const selectedHeading = headings.find(heading => heading.id === headingId) ?? headings[0] ?? null;
  const requiresHeading = scope === 'section';
  const canSubmit =
    !disabled &&
    !isStreaming &&
    instruction.trim().length > 0 &&
    (!requiresHeading || selectedHeading !== null);

  const submitGeneration = (): void => {
    const trimmed = instruction.trim();
    if (trimmed.length === 0) return;
    if (scope === 'section' && selectedHeading === null) return;
    const body: AiGenerateBody =
      scope === 'document'
        ? { instruction: trimmed, scope }
        : {
            instruction: trimmed,
            scope,
            sectionAnchor: { headingPath: selectedHeading.headingPath },
          };
    setInstruction('');
    void onGenerate(body).catch(() => undefined);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="inline-flex w-fit rounded-md border border-border bg-surface/60 p-1">
        {(['document', 'section'] as const).map(option => (
          <button
            key={option}
            type="button"
            disabled={disabled || isStreaming}
            aria-pressed={scope === option}
            className={
              scope === option
                ? 'rounded-sm bg-[oklch(0.72_0.14_225/0.15)] px-3 py-1 text-xs font-medium text-accent-bright'
                : 'rounded-sm px-3 py-1 text-xs font-medium text-text-secondary hover:text-text-primary'
            }
            onClick={() => {
              setScope(option);
            }}
          >
            {option === 'document' ? 'Document' : 'Section'}
          </button>
        ))}
      </div>
      {scope === 'section' && (
        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          <span>Section</span>
          <select
            value={selectedHeading?.id ?? ''}
            disabled={disabled || isStreaming || headings.length === 0}
            onChange={event => {
              setHeadingId(event.target.value);
            }}
            className="h-9 rounded-md border border-border bg-surface px-2 text-sm text-text-primary"
          >
            {headings.length === 0 ? (
              <option value="">No headings</option>
            ) : (
              headings.map(heading => (
                <option key={heading.id} value={heading.id}>
                  {heading.label}
                </option>
              ))
            )}
          </select>
        </label>
      )}
      <label className="sr-only" htmlFor="artifact-ai-generate-instruction">
        Generate instruction
      </label>
      <Textarea
        id="artifact-ai-generate-instruction"
        value={instruction}
        disabled={disabled || isStreaming}
        placeholder="Generate a revision"
        onChange={event => {
          setInstruction(event.target.value);
        }}
        className="min-h-28"
      />
      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          disabled={!canSubmit}
          className="inline-flex gap-1.5"
          onClick={submitGeneration}
        >
          <Sparkles className="size-4" />
          {isStreaming ? 'Generating…' : 'Generate'}
        </Button>
      </div>
    </div>
  );
}
